import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { Upload, Zap, Monitor, Smartphone, Volume2, CheckCircle, ArrowRight, Film, RefreshCw, ImageIcon, Sparkles, Play, X, ChevronDown, ChevronLeft, ChevronRight, Plus, Loader2, Info, Lock, Settings, Youtube, Star, CloudRain } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthModal from '@/components/AuthModal';
import RatingBar from '@/components/RatingBar';
import ExampleVideosRow from '@/components/ExampleVideosRow';
// Code-split the per-tool tab panels so their JS only downloads when the user
// opens that tool — keeps the initial Generate page bundle small/fast.
const RainCanvasTab = lazy(() => import('@/components/RainCanvasTab'));
const AtmosphereTab = lazy(() => import('@/components/AtmosphereTab'));
const Upscale4KTab = lazy(() => import('@/components/Upscale4KTab'));
const TransformTab = lazy(() => import('@/components/TransformTab'));
const ImageGeneratorTab = lazy(() => import('@/components/ImageGeneratorTab'));
import { PRICING_CONFIG } from '@/config/pricing';
import useSeo from '@/hooks/useSeo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lightweight fallback shown while a lazily-loaded tool panel downloads.
const TabLoading = () => (
  <div className="flex items-center justify-center py-20" data-testid="tab-loading">
    <Loader2 className="w-8 h-8 text-[#FFB238] animate-spin" />
  </div>
);

const VEO_CREDITS_FALLBACK = 450;
const VEO_DURATION = 8; // Superior Motion (Seedance v1 Pro on fal.ai) — default 8s
const LOOP_CREDITS = 0;

const IMAGE_CREDITS_FALLBACK = { '1080p': 20, '1440p': 40, '4k': 60 };

// Per-route JSON-LD for the Transform Image service. Module-scope so the object
// identity stays stable across renders (keeps useSeo's effect from re-firing).
const TRANSFORM_JSONLD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebApplication',
      name: 'AI Image Transformation — Visual Atmos',
      url: 'https://visualatmos.com/transform',
      applicationCategory: 'MultimediaApplication',
      operatingSystem: 'Web',
      description:
        'Change the season, time of day or weather in any photo with AI. Turn summer into winter, day into night, or add rain, snow and fog — up to 4K, powered by Gemini 3 Pro Image on Vertex AI.',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        description: 'Free signup includes starter credits. Transformations from 20 credits.',
      },
      featureList: [
        'Change the weather in a photo — add rain, snow, fog or clear skies',
        'Change the season of an image — summer to autumn, winter or spring',
        'Change the time of day — turn day into night, golden hour or blue hour',
        'Custom text prompt for fine control',
        'Output up to 4K resolution',
        'Commercial usage license included',
      ],
    },
    {
      '@type': 'HowTo',
      name: 'How to change the weather, season or time of day in a photo',
      description:
        'Transform any image into a new season, time of day or weather in three steps with Visual Atmos.',
      step: [
        { '@type': 'HowToStep', position: 1, name: 'Upload your image', text: 'Upload a JPG, PNG or WEBP (up to 20MB), or pick one from your gallery.' },
        { '@type': 'HowToStep', position: 2, name: 'Pick an atmosphere', text: 'Choose a season, time of day or weather — or describe the look you want in the custom field.' },
        { '@type': 'HowToStep', position: 3, name: 'Transform', text: 'Generate the new image at up to 4K. It is saved to your gallery and ready to download.' },
      ],
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Can I change the weather in a photo?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Visual Atmos adds realistic rain, snow, fog or clear skies to any photo while keeping the main subject and composition intact.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I change the season of an image?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes — turn a summer scene into autumn, winter or spring in one click. The AI reinterprets foliage, lighting and colour to match the season you pick.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I turn day into night in a picture?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Pick a time of day — day, golden hour, blue hour or night — and the tool relights the scene to match, including warm or cool colour grading.',
          },
        },
        {
          '@type': 'Question',
          name: 'What resolution are the transformed images?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'You can transform images up to 4K. Results are saved to your gallery and include a commercial usage license.',
          },
        },
      ],
    },
  ],
};

function Generate() {
  const { user, token, fetchUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Live video pricing (admin-configurable) — per-second rates. Cost = rate × duration.
  const [klingPerSec, setKlingPerSec] = useState({ '1080p': 25, '720p': 20 });
  const [veoPerSec, setVeoPerSec] = useState({ '1080p': 75, '720p': 45 });
  // 4K = 1080p base + upscale surcharge (matches server-side pricing).
  // Kept as state so admin-configurable upscale prices flow through here
  // instead of being hardcoded — surcharge brackets: ≤10s / ≤20s / ≤30s.
  const [upscaleCredits, setUpscaleCredits] = useState({ '10s': 150, '20s': 300, '30s': 450 });
  const [imageCredits, setImageCredits] = useState(IMAGE_CREDITS_FALLBACK);
  useEffect(() => {
    axios.get(`${API}/video-pricing`).then(res => {
      const p = res.data || {};
      if (typeof p.kling_per_sec_1080 === 'number') setKlingPerSec({ '1080p': p.kling_per_sec_1080, '720p': p.kling_per_sec_720 });
      if (typeof p.veo_per_sec_1080 === 'number') setVeoPerSec({ '1080p': p.veo_per_sec_1080, '720p': p.veo_per_sec_720 });
      if (typeof p.upscale_10s === 'number') setUpscaleCredits({
        '10s': p.upscale_10s,
        '20s': p.upscale_20s,
        '30s': p.upscale_30s,
      });
      if (typeof p.img_1080p === 'number') setImageCredits({
        '1080p': p.img_1080p,
        '1440p': p.img_1440p,
        '4k': p.img_4k,
      });
    }).catch(() => { /* keep fallback */ });
  }, []);

  // Tab state — read from URL path (/loop, /image, /sound) or query param (?tab=)
  const [activeTab, setActiveTab] = useState(() => {
    const path = window.location.pathname.replace(/^\//, '');
    if (['loop', 'image', 'sound', 'stabilize', 'upscale', 'transform', 'rain'].includes(path)) return path;
    if (path === 'generate' || path === 'video') return 'generate';
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (['generate', 'loop', 'image', 'sound', 'stabilize', 'upscale', 'transform', 'rain'].includes(tab)) return tab;
    return 'loop';
  });

  // Per-route SEO: switch <title>/<description>/canonical based on which tab
  // the user is on. Each tab targets a different long-tail keyword so /loop
  // and /generate don't compete for the same Google result.
  const SEO_BY_TAB = {
    loop: {
      title: 'Free Video Loop Maker — Seamless Loops in Seconds | Visual Atmos',
      description: 'Turn any short clip into a seamlessly looping video in seconds. Free, browser-based, no watermarks. Perfect for streamers, sleep apps and cozy ambient backgrounds.',
      canonical: 'https://visualatmos.com/loop',
    },
    generate: {
      title: 'AI Video Generator with Stable Camera — Cozy Ambient Videos | Visual Atmos',
      description: 'Generate cinematic AI videos with a locked, stable camera. Cozy fireplaces, rainy windows, snowy cabins and nature scenes. Powered by Kling 3.0 and Seedance 2.0.',
      canonical: 'https://visualatmos.com/',
    },
    stabilize: {
      title: 'Video Stabilizer — Lock Camera, Remove Shake | Visual Atmos',
      description: 'Stabilize shaky videos and lock the camera for that ambient, locked-off look. Browser-based, no install required.',
      canonical: 'https://visualatmos.com/stabilize',
    },
    image: {
      title: 'AI Image Generator — Cozy Backgrounds & Stills | Visual Atmos',
      description: 'Generate high-resolution cozy AI images: fireplaces, libraries, rainy cafés, snowy cabins. Up to 4K, commercial license included.',
      canonical: 'https://visualatmos.com/image',
    },
    sound: {
      title: 'Ambient Sound Effects Generator | Visual Atmos',
      description: 'Generate ambient sound effects — fireplace crackle, rainfall, forest ambience. Pair them with looping videos for a complete cozy experience.',
      canonical: 'https://visualatmos.com/sound',
    },
    atmosphere: {
      title: 'Atmospheric Overlays — Add Rain, Snow & Fog to Any Scene | Visual Atmos',
      description: 'Drop realistic rain, snow or fog onto your videos and photos. Browser-based, 1080p output, seamlessly looping. Perfect for cozy ambient channels.',
      canonical: 'https://visualatmos.com/atmosphere',
    },
    upscale: {
      title: '4K Video Upscaler — Topaz Proteus AI Enhancement | Visual Atmos',
      description: 'Upscale any short MP4 to 4K with Topaz Proteus. 200 credits per video, fixed. Perfect for AI clips, screen captures, and old footage.',
      canonical: 'https://visualatmos.com/upscale',
    },
    transform: {
      title: 'Change Weather, Season & Time of Day in a Photo — AI Image Transformation | Visual Atmos',
      description: 'Change the weather, season or time of day in any photo with AI. Add rain, snow or fog, turn summer into winter, or day into night — up to 4K, powered by Gemini 3 Pro Image on Vertex AI.',
      canonical: 'https://visualatmos.com/transform',
      ogUrl: 'https://visualatmos.com/transform',
      jsonLd: TRANSFORM_JSONLD,
    },
  };
  useSeo(SEO_BY_TAB[activeTab] || SEO_BY_TAB.generate);

  // Shared
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Generate Video state
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [preloadedImageId, setPreloadedImageId] = useState(null); // set when user clicks "Animate" in gallery — skips re-upload
  // Set when user clicks "Transform" on a gallery image — passed straight to TransformTab.
  const [preloadedTransform, setPreloadedTransform] = useState(null); // { imageId, previewUrl, fileName }
  const [prompt, setPrompt] = useState('');
  const [videoDuration, setVideoDuration] = useState(4);
  const [videoModel, setVideoModel] = useState('kling'); // 'kling' (Steady Vibe) or 'veo' (Living Motion)
  const [generating, setGenerating] = useState(false);
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [videoResolution, setVideoResolution] = useState('1080p'); // "720p" | "1080p"
  const [resolution, setResolution] = useState('1080p');
  const [audio, setAudio] = useState(false);
  const imageInputRef = useRef(null);

  // Loop Video state
  const [loopVideo, setLoopVideo] = useState(null);
  const [loopVideoName, setLoopVideoName] = useState('');
  const [loopVideoId, setLoopVideoId] = useState(null);

  // Static-Lock (stabilize) state
  const STABILIZE_CREDITS = 20;
  const [stabVideo, setStabVideo] = useState(null);
  const [stabVideoName, setStabVideoName] = useState('');
  const [stabilizing, setStabilizing] = useState(false);
  const [stabUploadProgress, setStabUploadProgress] = useState(0);
  const [looping, setLooping] = useState(false);
  const videoInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Image Generator state
  const [imgPrompt, setImgPrompt] = useState('');
  const [enhancing, setEnhancing] = useState(false);

  const handleEnhancePrompt = async () => {
    if (!checkAuth()) return;
    const text = imgPrompt.trim();
    if (text.length < 2) {
      toast.error('Add a few keywords first, then tap Enhance.');
      return;
    }
    setEnhancing(true);
    try {
      const res = await axios.post(`${API}/images/enhance-prompt`, { prompt: text }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.prompt) {
        setImgPrompt(res.data.prompt);
        toast.success('Prompt enhanced — feel free to tweak it.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not enhance the prompt. Please try again.');
    } finally {
      setEnhancing(false);
    }
  };
  const [imgAspectRatio, setImgAspectRatio] = useState('16:9');
  const [imgResolution, setImgResolution] = useState('1440p');
  const [imgStyle, setImgStyle] = useState('creative');
  const [imgGenerating, setImgGenerating] = useState(false);

  // Sound Generator state
  const [soundPrompt, setSoundPrompt] = useState('');
  const [soundDuration, setSoundDuration] = useState(10);
  const [soundGenerating, setSoundGenerating] = useState(false);

  // Contact form state
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSending, setContactSending] = useState(false);

  // Prompt lock state
  const [promptLocked, setPromptLocked] = useState(false);
  const [promptLockedUntil, setPromptLockedUntil] = useState(null);

  useEffect(() => {
    if (user && token) {
      axios.get(`${API}/auth/prompt-status`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          setPromptLocked(res.data.locked);
          setPromptLockedUntil(res.data.locked_until);
        }).catch(() => {});
    }
  }, [user, token]);

  const handleGenerationError = (error) => {
    if (error.response?.status === 403) {
      toast.error(error.response.data.detail, { duration: 8000 });
      // Re-check lock status
      axios.get(`${API}/auth/prompt-status`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          setPromptLocked(res.data.locked);
          setPromptLockedUntil(res.data.locked_until);
        }).catch(() => {});
      return true;
    }
    return false;
  };

  const SOUND_CREDITS = { 10: 30, 15: 40, 20: 50, 60: 70, 300: 120, 1200: 200 };

  // Example videos state
  const [exampleVideos, setExampleVideos] = useState([]);
  const [previewExampleVideo, setPreviewExampleVideo] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  // Live site rating (shown in hero social-proof pill) — always truthful
  const [siteRating, setSiteRating] = useState(null);

  // Whether the signed-in user has rated yet — drives where the rating bar sits.
  // null = unknown/guest (keep bottom), false = not rated (show prominent at top),
  // true = rated (keep bottom so they can still change it).
  const [userHasRated, setUserHasRated] = useState(null);

  // Concurrent generation limit state
  const [activeGenerations, setActiveGenerations] = useState(0);
  const [maxConcurrent, setMaxConcurrent] = useState(1);
  const atConcurrentLimit = user ? activeGenerations >= maxConcurrent : false;

  // Insufficient credits modal state
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [creditsNeeded, setCreditsNeeded] = useState(0);
  const [topupLoading, setTopupLoading] = useState(null);

  const imgCreditCost = imageCredits[imgResolution];

  // Fetch generation status (active count + tier limit)
  const fetchGenerationStatus = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/generation-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveGenerations(res.data.active_count);
      setMaxConcurrent(res.data.max_concurrent);
    } catch (e) {
      console.error('Generation status poll failed:', e);
    }
  };

  useEffect(() => {
    if (user && token) {
      fetchGenerationStatus();
      const interval = setInterval(fetchGenerationStatus, 8000);
      return () => clearInterval(interval);
    }
  }, [user, token]);

  // Fetch example videos on mount
  useEffect(() => {
    axios.get(`${API}/examples`).then(res => {
      const all = res.data || [];
      // Shuffle and pick 6
      const shuffled = [...all].sort(() => Math.random() - 0.5);
      setExampleVideos(shuffled.slice(0, 15));
    }).catch(() => {});
  }, []);

  // Fetch live average rating for the hero social-proof pill
  useEffect(() => {
    axios.get(`${API}/ratings`).then(res => {
      if (res.data && typeof res.data.average === 'number') {
        setSiteRating(res.data.average);
      }
    }).catch(() => {});
  }, []);

  // Determine whether the signed-in user has rated (for rating-bar placement).
  useEffect(() => {
    if (!token) { setUserHasRated(null); return; }
    axios.get(`${API}/ratings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setUserHasRated(res.data?.my_score != null))
      .catch(() => {});
  }, [token]);

  const perSecRate = (() => {
    const rates = videoModel === 'veo' ? veoPerSec : klingPerSec;
    return rates[videoResolution === '720p' ? '720p' : '1080p'] || 0;
  })();
  const totalCredits = perSecRate * videoDuration;

  const handleTopupPurchase = async (packId) => {
    setTopupLoading(packId);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/payments/create-topup`,
        null,
        {
          params: { pack: packId, origin_url: originUrl },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout');
      setTopupLoading(null);
    }
  };

  const checkAuth = () => {
    if (!user) { setShowAuthModal(true); return false; }
    return true;
  };

  // Conversion CTA shown below each action button, visible only to logged-out users.
  // Points guests to the Starter plan (cheapest entry into paid tier).
  const GuestCTA = () => {
    if (user) return null;
    const starter = PRICING_CONFIG.plans.find(p => p.id === 'starter');
    if (!starter) return null;
    return (
      <button
        type="button"
        onClick={() => navigate('/credits')}
        className="w-full mt-2 px-4 py-3 rounded-lg bg-[#0F1419] border border-[#FFB238]/40 text-[#FFB238] font-medium text-sm hover:bg-[#FFB238]/10 hover:border-[#FFB238] transition-all flex items-center justify-center gap-2"
        data-testid="guest-upgrade-cta"
      >
        <Zap className="w-4 h-4" fill="#FFB238" />
        <span>Get {starter.credits.toLocaleString()} credits for ${starter.price}/mo</span>
        <ArrowRight className="w-4 h-4" />
      </button>
    );
  };

  // ===== GENERATE VIDEO HANDLERS =====
  const handleImageSelect = (e) => {
    if (!checkAuth()) return;
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
      if (file.size > 20 * 1024 * 1024) { toast.error('Image size must be less than 20MB'); return; }
      setImage(file);
      setPreloadedImageId(null);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        autoDetectAspectRatio(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Auto-detect portrait vs landscape from an image URL/dataURL and select the
  // matching aspect ratio (9:16 for portrait, 16:9 for landscape). Users can
  // still override afterwards — this only fires when a new image loads.
  const autoDetectAspectRatio = (src) => {
    if (!src) return;
    const img = new Image();
    img.onload = () => {
      if (img.naturalHeight > img.naturalWidth) {
        setAspectRatio('9:16');
      } else {
        setAspectRatio('16:9');
      }
    };
    img.src = src;
  };

  const handleGenerate = async () => {
    if (!checkAuth()) return;
    if (!image && !preloadedImageId) { toast.error('Please upload an image first'); return; }
    if (atConcurrentLimit) {
      toast.error('You have reached your concurrent generation limit. Please wait for your current renders to finish, or upgrade your plan.');
      return;
    }
    if (user.credits_balance < totalCredits) {
      setCreditsNeeded(totalCredits - user.credits_balance);
      setShowTopupModal(true);
      return;
    }
    setGenerating(true);
    try {
      let imageIdToUse;
      if (preloadedImageId) {
        // Image was prepared by the gallery "Animate" flow — already uploaded
        imageIdToUse = preloadedImageId;
      } else {
        const formData = new FormData();
        formData.append('file', image);
        const uploadResponse = await axios.post(`${API}/upload-image`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        imageIdToUse = uploadResponse.data.image_id;
      }
      await axios.post(`${API}/generate-video`, {
        prompt: prompt.trim(),
        image_id: imageIdToUse,
        aspect_ratio: aspectRatio,
        duration: videoDuration,
        model: videoModel,
        resolution: videoResolution,
      }, { headers: { Authorization: `Bearer ${token}` } });

      setPrompt(''); setImage(null); setImagePreview(null); setPreloadedImageId(null);
      setGenerating(false);
      setSuccessMessage('Video generation started! This usually takes 3-5 minutes — sometimes longer during peak hours. You can leave this page; the video will appear in your Gallery when ready.');
      setShowSuccessModal(true);
      fetchUser();
      fetchGenerationStatus();
    } catch (error) {
      if (!handleGenerationError(error)) {
        toast.error(error.response?.data?.detail || 'Failed to generate video');
      }
      if (error.response?.status === 429) fetchGenerationStatus();
      setGenerating(false);
    }
  };

  // ===== LOOP VIDEO HANDLERS =====
  // Read a video file's duration client-side via a hidden <video> element.
  const getVideoDuration = (file) => new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.onloadedmetadata = () => { const d = v.duration; URL.revokeObjectURL(url); resolve(d); };
      v.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      v.src = url;
    } catch { resolve(null); }
  });

  // Validate a loop video (type, size, min duration) then set it. Shared by the
  // file picker and the drag-and-drop zone so both paths enforce the 3s minimum.
  const acceptLoopVideo = async (file) => {
    if (!file) return;
    const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(mp4|mov)$/i)) {
      toast.error('Please select an MP4 or MOV file');
      return;
    }
    if (file.size === 0) {
      toast.error('This file appears empty (still being written by your phone?). Please try again in a few seconds.');
      return;
    }
    if (file.size > 100 * 1024 * 1024) { toast.error('Video must be under 100MB'); return; }
    const duration = await getVideoDuration(file);
    if (duration != null && duration <= 3) {
      toast.error('Looping only works with videos longer than 3 seconds.');
      return;
    }
    setLoopVideo(file);
    setLoopVideoName(file.name);
  };

  const handleVideoSelect = (e) => {
    if (!checkAuth()) return;
    acceptLoopVideo(e.target.files[0]);
  };

  // ===== STATIC-LOCK HANDLERS =====
  const handleStabVideoSelect = (e) => {
    if (!checkAuth()) return;
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('video/') && !file.name.match(/\.(mp4|mov)$/i)) {
      toast.error('Please select an MP4 or MOV file');
      return;
    }
    if (file.size > 100 * 1024 * 1024) { toast.error('Video must be under 100MB'); return; }
    setStabVideo(file);
    setStabVideoName(file.name);
  };

  const handleStabilize = async () => {
    if (!checkAuth()) return;
    if (!stabVideo) { toast.error('Please upload a video first'); return; }
    if (atConcurrentLimit) {
      toast.error('You have reached your concurrent generation limit. Please wait for your current renders to finish, or upgrade your plan.');
      return;
    }
    if (user.credits_balance < STABILIZE_CREDITS) {
      setCreditsNeeded(STABILIZE_CREDITS - user.credits_balance);
      setShowTopupModal(true);
      return;
    }
    setStabilizing(true);
    setStabUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', stabVideo);
      await axios.post(`${API}/videos/stabilize`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        onUploadProgress: (e) => {
          if (e.total) setStabUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
      setStabVideo(null); setStabVideoName('');
      setStabilizing(false);
      setSuccessMessage('Static-Lock processing started! This may take up to 3 minutes.');
      setShowSuccessModal(true);
      fetchUser();
      fetchGenerationStatus();
    } catch (error) {
      if (!handleGenerationError(error)) {
        toast.error(error.response?.data?.detail || 'Failed to process video');
      }
      setStabilizing(false);
    }
  };

  const handleLoop = async () => {
    if (!checkAuth()) return;
    if (!loopVideo && !loopVideoId) { toast.error('Please upload a video first'); return; }
    if (atConcurrentLimit) {
      toast.error('You have reached your concurrent generation limit. Please wait for your current renders to finish, or upgrade your plan.');
      return;
    }
    if (user.credits_balance < LOOP_CREDITS) {
      setCreditsNeeded(LOOP_CREDITS - user.credits_balance);
      setShowTopupModal(true);
      return;
    }
    setLooping(true);
    setUploadProgress(0);
    try {
      if (loopVideoId && !loopVideo) {
        // Server-side loop — pass video ID instead of re-uploading
        await axios.post(`${API}/videos/loop-from-gallery`, null, {
          params: { video_id: loopVideoId },
          headers: { Authorization: `Bearer ${token}` },
          timeout: 300000
        });
      } else {
        // Force-materialize the file on mobile. Chrome/Android and Safari/iOS
        // both have quirks where a freshly-captured (or Photos-picked) File
        // object's blob isn't fully ready on the first submit — the initial
        // request hits the network with 0 bytes or is aborted, and only the
        // second attempt (after refresh) succeeds. Reading the file into an
        // ArrayBuffer here forces the OS to fully materialize it before we
        // build the multipart body, so the very first submit works.
        let uploadBlob = loopVideo;
        try {
          const buf = await loopVideo.arrayBuffer();
          if (!buf || buf.byteLength === 0) {
            toast.error('The video file appears empty. Please re-select it and try again.');
            setLooping(false);
            return;
          }
          uploadBlob = new Blob([buf], { type: loopVideo.type || 'video/mp4' });
        } catch (readErr) {
          console.warn('[loop] arrayBuffer() failed, falling back to raw File:', readErr);
          // fall through to raw File — worst case we hit the original bug
        }
        const formData = new FormData();
        formData.append('file', uploadBlob, loopVideo.name || 'video.mp4');
        await axios.post(`${API}/videos/loop`, formData, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 300000,
          onUploadProgress: (e) => {
            if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        });
      }
      setLoopVideo(null); setLoopVideoName(''); setLoopVideoId(null);
      setLooping(false);
      setSuccessMessage('Loop processing started! Usually takes 1-3 minutes for typical clips. Larger files (30MB+) or longer videos may take up to 5 minutes.');
      setShowSuccessModal(true);
      fetchUser();
      fetchGenerationStatus();
    } catch (error) {
      console.error('[loop] submit failed:', error);
      if (!handleGenerationError(error)) {
        const detail = error.response?.data?.detail;
        const msg = detail || error.message || 'Failed to process loop';
        toast.error(msg);
      }
      if (error.response?.status === 429) fetchGenerationStatus();
      setLooping(false);
    }
  };

  // ===== IMAGE GENERATOR HANDLERS =====
  const handleImageGenerate = async () => {
    if (!checkAuth()) return;
    if (!imgPrompt.trim()) { toast.error('Please enter a prompt'); return; }
    if (atConcurrentLimit) {
      toast.error('You have reached your concurrent generation limit. Please wait for your current renders to finish, or upgrade your plan.');
      return;
    }
    if (user.credits_balance < imgCreditCost) {
      setCreditsNeeded(imgCreditCost - user.credits_balance);
      setShowTopupModal(true);
      return;
    }
    setImgGenerating(true);
    try {
      await axios.post(`${API}/images/generate`, {
        prompt: imgPrompt,
        aspect_ratio: imgAspectRatio,
        resolution: imgResolution,
        style: imgStyle
      }, { headers: { Authorization: `Bearer ${token}` } });

      setImgPrompt('');
      setImgGenerating(false);
      setSuccessMessage('Image generation started! Usually takes 15-30 seconds.');
      setShowSuccessModal(true);
      fetchUser();
      fetchGenerationStatus();
    } catch (error) {
      if (!handleGenerationError(error)) {
        toast.error(error.response?.data?.detail || 'Failed to generate image');
      }
      if (error.response?.status === 429) fetchGenerationStatus();
      setImgGenerating(false);
    }
  };

  const handleSoundGenerate = async () => {
    if (!checkAuth()) return;
    if (!soundPrompt.trim()) { toast.error('Please describe the sound you want'); return; }
    if (atConcurrentLimit) {
      toast.error('You have reached your concurrent generation limit.');
      return;
    }
    const cost = SOUND_CREDITS[soundDuration];
    if (user.credits_balance < cost) {
      setCreditsNeeded(cost - user.credits_balance);
      setShowTopupModal(true);
      return;
    }
    setSoundGenerating(true);
    try {
      await axios.post(`${API}/sounds/generate`, {
        prompt: soundPrompt,
        duration: soundDuration
      }, { headers: { Authorization: `Bearer ${token}` } });

      setSoundPrompt('');
      setSoundGenerating(false);
      setSuccessMessage(`Sound generation started! ${soundDuration >= 60 ? 'Usually takes 1-2 minutes for extended durations.' : 'Usually takes 30-60 seconds.'} Your sound will be auto-looped seamlessly.`);
      setShowSuccessModal(true);
      fetchUser();
      fetchGenerationStatus();
    } catch (error) {
      if (!handleGenerationError(error)) {
        toast.error(error.response?.data?.detail || 'Failed to generate sound');
      }
      if (error.response?.status === 429) fetchGenerationStatus();
      setSoundGenerating(false);
    }
  };


  // Handle "Send to Video Engine" from gallery — preload image
  useEffect(() => {
    const state = location.state;

    // When arriving from the gallery (Animate / Transform / Loop / Send to
    // Video Engine), jump straight to the tool instead of the hero so the user
    // lands exactly on the spot they need — no manual scrolling.
    const scrollToTools = () => setTimeout(() => {
      const el = document.querySelector('[data-testid="mode-tabs"]');
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - 80; // offset for top nav
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    }, 150);

    if (state?.fromImage && state?.imageId && state?.imagePreviewUrl && token) {
      setActiveTab('generate');
      setImagePreview(state.imagePreviewUrl);
      autoDetectAspectRatio(state.imagePreviewUrl);
      // Skip re-fetching the bytes: backend already created a temp_image we can
      // use directly via /generate-video. The fetch+File approach broke due to
      // GCS CORS on signed URLs; using the preloaded image_id is faster anyway.
      setPreloadedImageId(state.imageId);
      scrollToTools();
      // Clear state so it doesn't re-trigger
      window.history.replaceState({}, document.title);
    }
    if (state?.fromLoop && state?.loopVideoId) {
      setActiveTab('loop');
      setLoopVideoName(state.loopVideoName || 'video.mp4');
      setLoopVideoId(state.loopVideoId);
      scrollToTools();
      window.history.replaceState({}, document.title);
    }
    if (state?.fromTransformImage && state?.imageId) {
      setActiveTab('transform');
      setPreloadedTransform({
        imageId: state.imageId,
        previewUrl: state.imagePreviewUrl,
        fileName: state.fileName,
      });
      scrollToTools();
      window.history.replaceState({}, document.title);
    }
  }, [location.state, token]);

  return (
    <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)}
        onSuccess={() => { setShowAuthModal(false); window.location.reload(); }} />

      {/* Insufficient Credits Modal */}
      {showTopupModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowTopupModal(false)} data-testid="topup-modal">
          <div className="bg-[#1A2035] rounded-2xl border border-[#222D4A] max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-red-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Insufficient Credits</h2>
                <p className="text-gray-400">
                  You need <span className="text-white font-semibold">{creditsNeeded} more credits</span> to generate this.
                </p>
              </div>

              {user && ['starter', 'plus', 'pro'].includes(user.subscription_tier) ? (
                <>
                  {/* Dynamic Upsell — show next tier upgrade (except Pro) */}
                  {user.subscription_tier !== 'pro' && (() => {
                    const nextTier = user.subscription_tier === 'starter'
                      ? PRICING_CONFIG.plans.find(p => p.id === 'plus')
                      : PRICING_CONFIG.plans.find(p => p.id === 'pro');
                    if (!nextTier) return null;
                    return (
                      <div className="mb-6">
                        <p className="text-center text-sm text-gray-500 mb-4">Upgrade for more credits instantly</p>
                        <button
                          onClick={() => { setShowTopupModal(false); navigate('/credits'); }}
                          className="w-full px-6 py-5 rounded-xl bg-gradient-to-r from-[#FFB238]/20 to-[#FFDA82]/10 border border-[#FFB238]/40 hover:border-[#FFB238] transition-all"
                          data-testid={`upgrade-modal-${nextTier.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="text-left">
                              <p className="text-[#FFB238] font-bold text-lg">Upgrade to {nextTier.name}</p>
                              <p className="text-gray-300 text-sm">{nextTier.credits.toLocaleString()} credits/month — get your full balance instantly</p>
                              {nextTier.bonus && <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 text-xs font-semibold">{nextTier.bonus}</span>}
                            </div>
                            <span className="text-[#FFB238] font-bold text-xl ml-4">${nextTier.price}/mo</span>
                          </div>
                        </button>
                      </div>
                    );
                  })()}

                  {/* Top-up packs — always visible */}
                  <div>
                    <p className="text-center text-sm text-gray-500 mb-4">
                      {user.subscription_tier !== 'pro' ? 'Or grab a one-time top-up' : 'Grab a one-time top-up — these credits never expire.'}
                    </p>
                    <div className="space-y-3">
                      {PRICING_CONFIG.topups.map((pack) => (
                        <button
                          key={pack.id}
                          onClick={() => handleTopupPurchase(pack.id)}
                          disabled={topupLoading !== null}
                          className="w-full flex items-center justify-between px-6 py-4 rounded-xl bg-[#222D4A] border border-[#2A3550] hover:border-[#FFB238]/50 transition-all disabled:opacity-50"
                          data-testid={`topup-modal-${pack.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-lg bg-[#FFB238]/10 flex items-center justify-center">
                              <Plus className="w-5 h-5 text-[#FFB238]" />
                            </div>
                            <div className="text-left">
                              <p className="text-white font-semibold">{pack.name}</p>
                              <p className="text-gray-400 text-sm">{pack.credits.toLocaleString()} Credits</p>
                            </div>
                          </div>
                          <div className="text-right">
                            {topupLoading === pack.id ? (
                              <Loader2 className="w-5 h-5 text-[#FFB238] animate-spin" />
                            ) : (
                              <span className="text-[#FFB238] font-bold text-lg">${pack.price}</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-center text-sm text-gray-500 mb-6">Subscribe to a plan to get monthly credits and start generating.</p>
                  <div className="space-y-3">
                    {PRICING_CONFIG.plans.map((plan) => (
                      <button
                        key={plan.id}
                        onClick={() => { setShowTopupModal(false); navigate('/credits'); }}
                        className="w-full flex items-center justify-between px-6 py-4 rounded-xl bg-[#222D4A] border border-[#2A3550] hover:border-[#FFB238]/50 transition-all"
                        data-testid={`plan-modal-${plan.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-[#FFB238]/10 flex items-center justify-center">
                            <Zap className="w-5 h-5 text-[#FFB238]" />
                          </div>
                          <div className="text-left">
                            <p className="text-white font-semibold">{plan.name}</p>
                            <p className="text-gray-400 text-sm">{plan.credits.toLocaleString()} Credits / month</p>
                          </div>
                        </div>
                        <span className="text-[#FFB238] font-bold text-lg">${plan.price}/mo</span>
                      </button>
                    ))}
                  </div>
                </>
              )}

              <button
                onClick={() => setShowTopupModal(false)}
                className="w-full mt-4 px-6 py-3 rounded-lg bg-transparent text-gray-400 font-medium hover:text-white transition-colors text-sm"
                data-testid="topup-modal-close"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="success-modal">
          <div className="bg-[#1A2035] rounded-2xl p-6 sm:p-10 border border-[#222D4A] max-w-lg w-full text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">
              {activeTab === 'image' ? 'Image Generation Started!' : activeTab === 'sound' ? 'Sound Generation Started!' : activeTab === 'generate' ? 'Video Generation Started!' : activeTab === 'transform' ? 'Image Transformation Started!' : 'Loop Processing Started!'}
            </h2>
            <p className="text-gray-300 mb-2">{successMessage}</p>
            <p className="text-gray-400 text-sm mb-8">
              {activeTab === 'transform'
                ? 'You can track progress in My Gallery → Images.'
                : 'You can track progress in My Gallery.'}
            </p>
            <div className="flex flex-col gap-3">
              <button onClick={() => {
                  setShowSuccessModal(false);
                  // Image / Transform tools deep-link to the Images tab; the
                  // rest of the services land on the default Videos tab.
                  const galleryTab = (activeTab === 'transform' || activeTab === 'image') ? 'images' : undefined;
                  navigate('/my-gallery', galleryTab ? { state: { tab: galleryTab } } : {});
                }}
                className="w-full px-6 py-4 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold text-lg hover:bg-[#FFDA82] transition-colors flex items-center justify-center gap-2"
                data-testid="go-to-my-videos-button">
                Go to My Gallery <ArrowRight className="w-5 h-5" />
              </button>
              <button onClick={() => setShowSuccessModal(false)}
                className="w-full px-6 py-3 rounded-lg bg-[#222D4A] text-gray-300 font-medium hover:bg-[#2A3550] transition-colors border border-[#2A3550]"
                data-testid="stay-button">
                {activeTab === 'image' ? 'Generate Another Image' : activeTab === 'sound' ? 'Generate Another Sound' : activeTab === 'generate' ? 'Generate Another' : activeTab === 'transform' ? 'Transform Another Image' : 'Loop Another Video'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="relative text-center mb-8 sm:mb-12 px-4 font-body overflow-hidden" data-testid="hero-section">
        {/* warm radial glow behind the headline */}
        <div
          aria-hidden="true"
          className="animate-hero-glow pointer-events-none absolute left-1/2 top-[42%] h-[300px] w-[90vw] max-w-[540px] -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(255,178,56,0.20) 0%, rgba(255,178,56,0.06) 42%, transparent 72%)',
            filter: 'blur(42px)',
            zIndex: 0,
          }}
        />

        <div className="relative z-10 flex flex-col items-center">
          {/* Social proof cluster */}
          <div
            className="animate-hero-up inline-flex flex-wrap items-center justify-center gap-2.5 rounded-full border border-[#283452] bg-[#161C2E]/80 py-1.5 pl-1.5 pr-4 shadow-lg backdrop-blur-md"
            style={{ animationDelay: '0ms' }}
            data-testid="hero-social-proof"
          >
            <img src="/profile-pic.png" alt="Visual Laurence" className="h-7 w-7 rounded-full object-cover ring-2 ring-[#FFB238]/60" data-testid="founder-avatar" />
            <span className="text-xs font-medium text-slate-200 flex items-center gap-1.5">
              by <span className="text-white font-semibold">Visual Laurence</span>
              <a
                href="https://www.youtube.com/@visuallaurence"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 rounded-full bg-[#FF0000]/10 hover:bg-[#FF0000]/20 border border-[#FF0000]/30 px-2 py-0.5 text-[11px] font-semibold text-[#FF6B6B] transition-colors"
                data-testid="hero-youtube-link"
                aria-label="Visit Visual Laurence on YouTube"
              >
                <Youtube className="w-3.5 h-3.5" /> 14K
              </a>
            </span>
            <span className="h-1 w-1 rounded-full bg-slate-600" />
            <span className="flex items-center gap-1" data-testid="hero-star-rating">
              <span className="flex">{[...Array(5)].map((_, i) => (<Star key={i} className="w-3.5 h-3.5 fill-[#FBBF24] text-[#FBBF24]" />))}</span>
              <span className="text-xs font-semibold text-amber-200">{siteRating != null ? siteRating.toFixed(1) : '4.6'}</span>
            </span>
          </div>

          {/* Headline */}
          <h1
            className="font-display mt-6 text-5xl sm:text-6xl md:text-7xl font-semibold leading-[1.08] tracking-tight animate-hero-up"
            style={{ color: '#FDF6E8', animationDelay: '80ms' }}
            data-testid="hero-headline"
          >
            The Complete Ambient<br className="hidden sm:block" /> Video Studio
          </h1>

          <p className="animate-hero-up mt-5 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed" style={{ animationDelay: '150ms' }}>
            Videos, images, and loops — all in one place.
          </p>
          <p className="animate-hero-up mt-3 text-xs sm:text-sm text-slate-500" style={{ animationDelay: '200ms' }}>
            Ideal for YouTube Ambience Channels · Twitch Backgrounds
          </p>
        </div>
      </div>

      {/* ===== EXAMPLE VIDEOS — Netflix-style compact row with edge-gradient arrows ===== */}
      <div className="text-center mb-3 px-4 font-body" data-testid="example-videos-label">
        <p className="text-sm sm:text-base text-slate-200 font-semibold inline-flex items-center justify-center gap-2">
          <Sparkles className="w-4 h-4 text-[#FFB238]" /> Made with Visual Atmos
        </p>
        <p className="text-xs text-slate-500 mt-1">A few clips created by our community — for inspiration.</p>
      </div>
      <ExampleVideosRow videos={exampleVideos} onSelect={setPreviewExampleVideo} />

      {/* Tab Buttons — single row: Loop, Generate Video, Image Generator. Sound Effects hidden. */}
      <div className="max-w-5xl mx-auto mb-8 sm:mb-10 px-4 font-body" data-testid="mode-tabs">
        <div className="flex flex-wrap justify-center gap-3 sm:gap-4 pt-2">
          <button
            onClick={() => setActiveTab('rain')}
            className={`relative flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-10 py-3 sm:py-4 rounded-xl text-sm sm:text-base font-bold transition-all border-2 w-[calc(50%-0.375rem)] sm:w-[272px] ${
              activeTab === 'rain'
                ? 'bg-[#FFB238] text-[#0F1419] border-[#FFB238] shadow-lg shadow-[#FFB238]/20'
                : 'bg-[#1A2035] text-gray-300 border-[#222D4A] hover:border-[#FFB238]/50'
            }`}
            data-testid="tab-rain"
          >
            <span
              className="absolute -top-4 -right-2 pointer-events-none select-none"
              style={{ textShadow: '0 0 6px rgba(34,211,238,0.95), 0 0 18px rgba(34,211,238,0.55)' }}
            >
              <span className="text-base font-black italic uppercase tracking-wider" style={{ color: '#67E8F9' }}>New</span>
              <span className="text-lg font-black italic text-white ml-0.5">!</span>
            </span>
            <CloudRain className="w-4 h-4 sm:w-5 sm:h-5" />
            Rain Video Canvas
          </button>
          <button
            onClick={() => setActiveTab('loop')}
            className={`flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-10 py-3 sm:py-4 rounded-xl text-sm sm:text-base font-bold transition-all border-2 relative w-[calc(50%-0.375rem)] sm:w-[272px] ${
              activeTab === 'loop'
                ? 'bg-[#FFB238] text-[#0F1419] border-[#FFB238] shadow-lg shadow-[#FFB238]/20'
                : 'bg-[#1A2035] text-gray-300 border-[#222D4A] hover:border-[#FFB238]/50'
            }`}
            data-testid="tab-loop"
          >
            <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
            Make it Loop
            <span
              className="absolute -top-4 -right-2 pointer-events-none select-none"
              style={{
                textShadow: '0 0 6px rgba(34,211,238,0.95), 0 0 18px rgba(34,211,238,0.55)',
              }}
            >
              <span className="text-base font-black italic uppercase tracking-wider" style={{ color: '#67E8F9' }}>Free</span>
              <span className="text-lg font-black italic text-white ml-0.5">!</span>
            </span>
          </button>
          <button
            onClick={() => setActiveTab('generate')}
            className={`relative flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-10 py-3 sm:py-4 rounded-xl text-sm sm:text-base font-bold transition-all border-2 w-[calc(50%-0.375rem)] sm:w-[272px] ${
              activeTab === 'generate'
                ? 'bg-[#FFB238] text-[#0F1419] border-[#FFB238] shadow-lg shadow-[#FFB238]/20'
                : 'bg-[#1A2035] text-gray-300 border-[#222D4A] hover:border-[#FFB238]/50'
            }`}
            data-testid="tab-generate"
          >
            <span
              className="absolute -top-4 -right-2 pointer-events-none select-none"
              style={{
                textShadow: '0 0 6px rgba(34,211,238,0.95), 0 0 18px rgba(34,211,238,0.55)',
              }}
            >
              <span className="text-base font-black italic uppercase tracking-wider" style={{ color: '#67E8F9' }}>Popular</span>
              <span className="text-lg font-black italic text-white ml-0.5">!</span>
            </span>
            <Film className="w-4 h-4 sm:w-5 sm:h-5" />
            Generate Video
          </button>
        </div>

        {/* Second row — Image Generator, Video Upscale, Transform Image */}
        <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mt-3 sm:mt-4">
          <button
            onClick={() => setActiveTab('image')}
            className={`flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-10 py-3 sm:py-4 rounded-xl text-sm sm:text-base font-bold transition-all border-2 w-[calc(50%-0.375rem)] sm:w-[272px] ${
              activeTab === 'image'
                ? 'bg-[#FFB238] text-[#0F1419] border-[#FFB238] shadow-lg shadow-[#FFB238]/20'
                : 'bg-[#1A2035] text-gray-300 border-[#222D4A] hover:border-[#FFB238]/50'
            }`}
            data-testid="tab-image"
          >
            <ImageIcon className="w-4 h-4 sm:w-5 sm:h-5" />
            Image Generator
          </button>
          <button
            onClick={() => setActiveTab('upscale')}
            className={`flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-10 py-3 sm:py-4 rounded-xl text-sm sm:text-base font-bold transition-all border-2 relative w-[calc(50%-0.375rem)] sm:w-[272px] ${
              activeTab === 'upscale'
                ? 'bg-[#FFB238] text-[#0F1419] border-[#FFB238] shadow-lg shadow-[#FFB238]/20'
                : 'bg-[#1A2035] text-gray-300 border-[#222D4A] hover:border-[#FFB238]/50'
            }`}
            data-testid="tab-upscale"
          >
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
            Video Upscale
          </button>
          <button
            onClick={() => setActiveTab('transform')}
            className={`flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-10 py-3 sm:py-4 rounded-xl text-sm sm:text-base font-bold transition-all border-2 relative w-[calc(50%-0.375rem)] sm:w-[272px] ${
              activeTab === 'transform'
                ? 'bg-[#FFB238] text-[#0F1419] border-[#FFB238] shadow-lg shadow-[#FFB238]/20'
                : 'bg-[#1A2035] text-gray-300 border-[#222D4A] hover:border-[#FFB238]/50'
            }`}
            data-testid="tab-transform"
          >
            <ImageIcon className="w-4 h-4 sm:w-5 sm:h-5" />
            Transform Image
          </button>
        </div>
      </div>

      {/* ===== RAIN VIDEO CANVAS TAB ===== */}
      {activeTab === 'rain' && (
        <div data-testid="rain-canvas-section">
          <div className="text-center mb-6 px-4">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white">The <span className="text-[#FFB238]">BEST</span> way to add rain to your image</h2>
            <p className="text-gray-400 text-sm mt-2 max-w-2xl mx-auto">Upload a photo, pick rain or snow, preview it live for free — pay only when you download your 30-second video.</p>
          </div>
          <Suspense fallback={<TabLoading />}>
            <RainCanvasTab API={API} token={token} onRequireAuth={checkAuth} onNavigateGallery={() => navigate('/my-gallery')} />
          </Suspense>
        </div>
      )}

      {/* ===== GENERATE VIDEO TAB ===== */}
      {activeTab === 'generate' && (
        <div className="max-w-6xl mx-auto">
        <div
          className="flex items-start gap-3 mb-6 rounded-xl border border-[#FFB238]/50 bg-[#FFB238]/10 px-4 py-3.5 sm:px-5"
          data-testid="ai-unpredictable-banner"
        >
          <Info className="w-5 h-5 text-[#FFB238] flex-shrink-0 mt-0.5" />
          <p className="text-sm sm:text-base text-[#FFE4B5] font-medium leading-snug">
            Heads up: this is AI — results vary and can be unpredictable. It won't always match your prompt exactly (an effect like rain may not appear every time).
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {/* Upload Image */}
          <div className="bg-[#1A2035] rounded-2xl p-5 sm:p-8 border border-[#222D4A]" data-testid="upload-section">
            <div className="flex items-center gap-3 mb-6">
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#FFB238] text-[#0F1419] text-base font-bold flex-shrink-0" data-testid="step-badge-1">1</span>
              <h2 className="text-2xl font-bold text-white">Upload Your Image</h2>
            </div>
            <p className="text-sm text-gray-400 mb-6">JPG, PNG, or WEBP — up to 20MB</p>

            <div className="border-2 border-dashed border-[#222D4A] rounded-xl p-12 text-center cursor-pointer hover:border-[#FFB238] transition-colors"
              onClick={() => { if (checkAuth()) imageInputRef.current?.click(); }}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); if (!checkAuth()) return; const f = e.dataTransfer.files[0]; if (f?.type.startsWith('image/')) { setImage(f); setPreloadedImageId(null); const r = new FileReader(); r.onloadend = () => { setImagePreview(r.result); autoDetectAspectRatio(r.result); }; r.readAsDataURL(f); } }}
              data-testid="image-upload-zone">
              {imagePreview ? (
                <div className="space-y-4">
                  <img src={imagePreview} alt="Preview" className="max-h-64 mx-auto rounded-lg" />
                  <p className="text-sm text-gray-300">{image?.name}</p>
                  <button onClick={(e) => { e.stopPropagation(); setImage(null); setImagePreview(null); setPreloadedImageId(null); }}
                    className="text-sm text-red-400 hover:text-red-300 transition-colors" data-testid="remove-image-button">Remove image</button>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full bg-[#222D4A] flex items-center justify-center mx-auto mb-4">
                    <Upload className="w-8 h-8 text-[#FFB238]" />
                  </div>
                  <p className="text-gray-300 mb-2">Drag & drop your image here</p>
                  <p className="text-sm text-gray-500">or click to browse your files</p>
                </>
              )}
            </div>

            {/* Aspect Ratio */}
            <div className="mt-6" data-testid="aspect-ratio-section">
              <label className="flex items-center gap-2 text-sm text-gray-400 mb-3">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#FFB238] text-[#0F1419] text-[11px] font-bold flex-shrink-0">2</span>
                Aspect Ratio
              </label>
              <div className="flex gap-3">
                <button onClick={() => setAspectRatio('16:9')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-all font-medium ${aspectRatio === '16:9' ? 'bg-[#FFB238] text-[#0F1419] border-[#FFB238]' : 'bg-[#222D4A] text-gray-300 border-[#2A3550] hover:border-[#FFB238]/50'}`}
                  data-testid="aspect-ratio-16-9">
                  <Monitor className="w-4 h-4" /><span>16:9</span><span className="text-xs opacity-70">YouTube</span>
                </button>
                <button onClick={() => setAspectRatio('9:16')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-all font-medium ${aspectRatio === '9:16' ? 'bg-[#FFB238] text-[#0F1419] border-[#FFB238]' : 'bg-[#222D4A] text-gray-300 border-[#2A3550] hover:border-[#FFB238]/50'}`}
                  data-testid="aspect-ratio-9-16">
                  <Smartphone className="w-4 h-4" /><span>9:16</span><span className="text-xs opacity-70">TikTok / Shorts</span>
                </button>
              </div>
            </div>

            {/* Video Resolution — 720p (cheap draft) vs 1080p (final) */}
            <div className="mt-6" data-testid="video-resolution-section">
              <label className="flex items-center gap-2 text-sm text-gray-400 mb-3">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#FFB238] text-[#0F1419] text-[11px] font-bold flex-shrink-0">3</span>
                Video Resolution
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={() => setVideoResolution('720p')}
                  className={`flex-1 flex flex-col items-start text-left gap-1 px-4 py-3 rounded-lg border transition-all font-medium ${videoResolution === '720p' ? 'bg-[#FFB238] text-[#0F1419] border-[#FFB238]' : 'bg-[#222D4A] text-gray-300 border-[#2A3550] hover:border-[#FFB238]/50'}`}
                  data-testid="video-resolution-720p">
                  <span className="font-bold text-left">720p</span>
                  <span className={`text-xs text-left ${videoResolution === '720p' ? 'opacity-80' : 'text-gray-400'}`}>Perfect for previewing how the video will look.</span>
                </button>
                <button onClick={() => setVideoResolution('1080p')}
                  className={`flex-1 flex flex-col items-start text-left gap-1 px-4 py-3 rounded-lg border transition-all font-medium ${videoResolution === '1080p' ? 'bg-[#FFB238] text-[#0F1419] border-[#FFB238]' : 'bg-[#222D4A] text-gray-300 border-[#2A3550] hover:border-[#FFB238]/50'}`}
                  data-testid="video-resolution-1080p">
                  <span className="font-bold text-left">1080p</span>
                  <span className={`text-xs text-left ${videoResolution === '1080p' ? 'opacity-80' : 'text-gray-400'}`}>Full quality — best for your final high-quality video.</span>
                </button>
              </div>
            </div>

            {/* Model Selector — Steady Vibe (Kling) vs Living Motion (Veo) */}
            <div className="mt-6" data-testid="model-selector-section">
              <label className="flex items-center gap-2 text-sm text-gray-400 mb-3">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#FFB238] text-[#0F1419] text-[11px] font-bold flex-shrink-0">4</span>
                Video Model
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  onClick={() => { setVideoModel('kling'); }}
                  className={`text-left px-4 py-3 rounded-lg border transition-all ${videoModel === 'kling' ? 'bg-[#FFB238] text-[#0F1419] border-[#FFB238]' : 'bg-[#222D4A] text-gray-300 border-[#2A3550] hover:border-[#FFB238]/50'}`}
                  data-testid="model-kling">
                  <div className="font-bold mb-1">Steady Vibe</div>
                  <div className={`text-xs ${videoModel === 'kling' ? 'opacity-80' : 'text-gray-400'}`}>No camera movement. Best for fire, rain, snowfall, steam, palm trees and waves.</div>
                </button>
                <button
                  onClick={() => { setVideoModel('veo'); }}
                  className={`text-left px-4 py-3 rounded-lg border transition-all ${videoModel === 'veo' ? 'bg-[#FFB238] text-[#0F1419] border-[#FFB238]' : 'bg-[#222D4A] text-gray-300 border-[#2A3550] hover:border-[#FFB238]/50'}`}
                  data-testid="model-veo">
                  <div className="font-bold mb-1">Superior Motion</div>
                  <div className={`text-xs ${videoModel === 'veo' ? 'opacity-80' : 'text-gray-400'}`}>Expensive, but overall a powerful model. Good for foliage animations. No camera movement.</div>
                </button>
              </div>
            </div>

            {/* Video Duration — slider 5–15s, per-second pricing */}
            <div className="mt-6" data-testid="video-duration-section">
              <label className="flex items-center gap-2 text-sm text-gray-400 mb-3">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#FFB238] text-[#0F1419] text-[11px] font-bold flex-shrink-0">5</span>
                Video Duration
              </label>
              <div className="bg-[#222D4A] border border-[#2A3550] rounded-lg px-4 py-4">
                <div className="flex items-baseline justify-between mb-3">
                  <span className="text-white font-bold text-lg" data-testid="video-duration-value">{videoDuration} seconds</span>
                  <span className="text-[#FFB238] font-semibold text-sm" data-testid="video-duration-cost">{totalCredits} credits</span>
                </div>
                <input
                  type="range"
                  min="4"
                  max="15"
                  step="1"
                  value={videoDuration}
                  onChange={(e) => setVideoDuration(parseInt(e.target.value, 10))}
                  className="w-full h-2 rounded-full appearance-none cursor-pointer accent-[#FFB238] bg-[#1A2035]"
                  data-testid="video-duration-slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>4s</span>
                  <span>10s</span>
                  <span>15s</span>
                </div>
                
              </div>
            </div>

            <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
          </div>

          {/* Generation Summary */}
          <div className="bg-[#1A2035] rounded-2xl p-5 sm:p-8 border border-[#222D4A]" data-testid="generation-summary">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-lg bg-[#FFB238]/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#FFB238]" />
              </div>
              <h2 className="text-2xl font-bold text-white">Generation Summary</h2>
            </div>
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Video Generation ({videoResolution})</span>
                <span className="text-white font-medium">{totalCredits} credits</span>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-[#222D4A]">
                <span className="text-white font-medium">Total cost</span>
                <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FFB238]">
                  <Zap className="w-4 h-4 text-[#0F1419]" fill="#0F1419" />
                  <span className="text-sm font-bold text-[#0F1419]">{totalCredits} Credits</span>
                </div>
              </div>
              <div className="flex items-center justify-between"><span className="text-gray-400">Your balance</span><span className="text-white font-medium">{user?.credits_balance || 0} credits</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-400">Resolution</span><span className="text-white font-medium" data-testid="summary-resolution">{videoResolution}</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-400">Duration</span><span className="text-white font-medium" data-testid="summary-duration">{videoDuration} seconds</span></div>
              <div className="pt-4 border-t border-[#222D4A]">
                <label className="flex items-center gap-2 text-white font-medium mb-2 text-sm">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#FFB238] text-[#0F1419] text-[11px] font-bold flex-shrink-0">6</span>
                  Describe your video
                </label>
                <div className="flex items-start gap-2 mb-3 rounded-lg border border-[#FFB238]/30 bg-[#FFB238]/10 px-3 py-2.5" data-testid="prompt-pro-tip">
                  <Sparkles className="w-4 h-4 text-[#FFB238] mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-100/90 leading-relaxed">
                    <span className="font-bold text-[#FFB238]">PRO tip:</span> Make sure the elements you want to animate (such as rain, snow or steam) are already clearly visible in your image.
                  </p>
                </div>
                <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
                  onFocus={() => { if (!checkAuth()) document.activeElement.blur(); }}
                  placeholder="fireplace flames, candle flicker, gentle steam from a mug, rainfall outside the window..."
                  className="w-full px-4 py-3 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none resize-none" rows={3}
                  data-testid="prompt-input" />
              </div>

              <button onClick={handleGenerate}
                disabled={generating || (!image && !preloadedImageId) || !prompt.trim() || atConcurrentLimit || promptLocked}
                className="w-full px-6 py-4 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold text-lg hover:bg-[#FFDA82] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="generate-video-button">
                {promptLocked ? 'Account Temporarily Locked' : generating ? 'Uploading & Generating...' : `Generate Video — ${totalCredits} Credits`}
              </button>
              <GuestCTA />
              <div className="flex items-center gap-1.5 justify-center mt-1 group relative">
                <Info className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-xs text-gray-500">Content policy applies</span>
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-[#222D4A] border border-[#2A3550] text-gray-300 text-xs rounded-lg px-3 py-2 w-64 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                  To comply with copyright laws, we do not allow the generation of trademarked characters or brands.
                </div>
              </div>
              {atConcurrentLimit && (
                <p className="text-sm text-amber-400 text-center" data-testid="concurrent-limit-warning">
                  You have reached your concurrent generation limit ({maxConcurrent}). Wait for current renders to finish, or upgrade your plan.
                </p>
              )}
            </div>
          </div>
        </div>
        </div>
      )}

      {/* ===== MAKE IT LOOP TAB ===== */}
      {activeTab === 'loop' && (
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Upload Video */}
          <div className="bg-[#1A2035] rounded-2xl p-5 sm:p-8 border border-[#222D4A]" data-testid="loop-upload-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-lg bg-[#FFB238]/10 flex items-center justify-center">
                <Upload className="w-5 h-5 text-[#FFB238]" />
              </div>
              <h2 className="text-2xl font-bold text-white">Upload Your Video</h2>
            </div>
            <p className="text-sm text-gray-400 mb-6">MP4 or MOV — up to 100MB</p>

            <div className="border-2 border-dashed border-[#222D4A] rounded-xl p-8 text-center cursor-pointer hover:border-[#FFB238] transition-colors"
              onClick={() => { if (checkAuth()) videoInputRef.current?.click(); }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); if (!checkAuth()) return; const f = e.dataTransfer.files[0]; if (f) { acceptLoopVideo(f); } }}
              data-testid="video-upload-zone">
              {loopVideoName ? (
                <div className="space-y-3">
                  <div className="w-16 h-16 rounded-full bg-[#222D4A] flex items-center justify-center mx-auto">
                    <Film className="w-8 h-8 text-[#FFB238]" />
                  </div>
                  <p className="text-white font-medium">{loopVideoName}</p>
                  <p className="text-sm text-gray-400">{loopVideo ? `${(loopVideo.size / 1024 / 1024).toFixed(1)} MB` : loopVideoId ? 'From your gallery' : ''}</p>
                  <button onClick={(e) => { e.stopPropagation(); setLoopVideo(null); setLoopVideoName(''); setLoopVideoId(null); }}
                    className="text-sm text-red-400 hover:text-red-300 transition-colors" data-testid="remove-video-button">Remove video</button>
                </div>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-full bg-[#222D4A] flex items-center justify-center mx-auto mb-3">
                    <Film className="w-7 h-7 text-[#FFB238]" />
                  </div>
                  <p className="text-gray-300 mb-1">Drag & drop your video here</p>
                  <p className="text-sm text-gray-500">or click to browse your files</p>
                </>
              )}
            </div>

            {/* Best results — below upload */}
            <div className="mt-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-[#FFB238] font-medium mb-1">Best results</p>
              <p className="text-sm text-gray-300">
                <strong className="text-white">MUST</strong> be at least 3 seconds long. Works best with videos that have <strong className="text-white">no camera movement</strong> — 
                such as ambience videos.
              </p>
            </div>

            <input ref={videoInputRef} type="file" accept="video/mp4,video/quicktime,.mp4,.mov" onChange={handleVideoSelect} className="hidden" />
          </div>

          {/* Loop Summary */}
          <div className="bg-[#1A2035] rounded-2xl p-5 sm:p-8 border border-[#222D4A]" data-testid="loop-summary">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-lg bg-[#FFB238]/10 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-[#FFB238]" />
              </div>
              <h2 className="text-2xl font-bold text-white">Loop Summary</h2>
            </div>

            <div className="space-y-5">
              <div className="flex items-center justify-between pt-1">
                <span className="text-white font-medium">Cost</span>
                <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500">
                  <span className="text-sm font-bold text-white">FREE</span>
                </div>
              </div>
              <div className="flex items-center justify-between"><span className="text-gray-400">Processing time</span><span className="text-white font-medium">~10 sec to 3 min</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-400">Output</span><span className="text-white font-medium">MP4</span></div>

              <button onClick={handleLoop}
                disabled={looping || (!loopVideo && !loopVideoId) || atConcurrentLimit}
                className="w-full px-6 py-4 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold text-lg hover:bg-[#FFDA82] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="loop-video-button">
                {looping ? (
                  <><div className="w-5 h-5 border-2 border-[#0F1419] border-t-transparent rounded-full animate-spin" /> {loopVideo && uploadProgress < 100 ? `Uploading... ${uploadProgress}%` : 'Processing...'}</>
                ) : (
                  <><RefreshCw className="w-5 h-5" /> Make it Loop — Free</>
                )}
              </button>
              <p className="text-xs text-gray-500 text-center">The final looped video will be 1-2 seconds shorter than the original.</p>
              {atConcurrentLimit && (
                <p className="text-sm text-amber-400 text-center" data-testid="loop-concurrent-limit-warning">
                  Concurrent generation limit reached ({maxConcurrent}). Wait for current renders to finish, or upgrade.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== STATIC-LOCK / STABILIZE TAB ===== */}
      {activeTab === 'stabilize' && (
        <>
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Upload Video */}
          <div className="bg-[#1A2035] rounded-2xl p-5 sm:p-8 border border-[#222D4A]">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#FFB238]/20 flex items-center justify-center">
                <Lock className="w-5 h-5 text-[#FFB238]" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">Upload Video</h2>
            </div>

            <input
              type="file"
              accept="video/mp4,video/quicktime,.mp4,.mov"
              onChange={handleStabVideoSelect}
              className="hidden"
              id="stab-video-upload"
              data-testid="stab-video-input"
            />

            {!stabVideo ? (
              <label
                htmlFor="stab-video-upload"
                className="relative aspect-video bg-[#0F1419] rounded-xl border-2 border-dashed border-[#2A3550] hover:border-[#FFB238] transition-all cursor-pointer flex flex-col items-center justify-center group"
              >
                <Lock className="w-12 h-12 text-gray-500 group-hover:text-[#FFB238] transition-colors mb-3" />
                <p className="text-white font-medium mb-1">Click to upload video</p>
                <p className="text-gray-400 text-sm">MP4 or MOV — up to 100MB, max 16 seconds</p>
              </label>
            ) : (
              <div className="bg-[#0F1419] rounded-xl border border-[#2A3550] p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#FFB238]/20 flex items-center justify-center flex-shrink-0">
                  <Film className="w-6 h-6 text-[#FFB238]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate" data-testid="stab-video-name">{stabVideoName}</p>
                  <p className="text-gray-400 text-sm">{(stabVideo.size / (1024 * 1024)).toFixed(1)} MB</p>
                </div>
                <button
                  onClick={() => { setStabVideo(null); setStabVideoName(''); }}
                  className="p-2 rounded-lg hover:bg-[#222D4A] transition-colors"
                  data-testid="stab-clear-button"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            )}
          </div>

          {/* Summary + Stabilize button */}
          <div className="bg-[#1A2035] rounded-2xl p-5 sm:p-8 border border-[#222D4A]">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="w-6 h-6 text-[#FFB238]" />
              <h2 className="text-xl sm:text-2xl font-bold text-white">Steady Frame Summary</h2>
            </div>

            <div className="space-y-5">
              <div className="flex items-center justify-between pt-1">
                <span className="text-white font-medium">Cost</span>
                <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FFB238]">
                  <Zap className="w-4 h-4 text-[#0F1419]" fill="#0F1419" />
                  <span className="text-sm font-bold text-[#0F1419]">{STABILIZE_CREDITS} Credits</span>
                </div>
              </div>
              <div className="flex items-center justify-between"><span className="text-gray-400">Your balance</span><span className="text-white font-medium">{user?.credits_balance || 0} credits</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-400">Processing time</span><span className="text-white font-medium">~30 sec to 3 min</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-400">Output</span><span className="text-white font-medium">MP4 (1080p)</span></div>

              <button
                onClick={handleStabilize}
                disabled={stabilizing || !stabVideo || atConcurrentLimit}
                className="w-full px-6 py-4 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold text-lg hover:bg-[#FFDA82] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="stabilize-button"
              >
                {stabilizing ? (
                  <><div className="w-5 h-5 border-2 border-[#0F1419] border-t-transparent rounded-full animate-spin" />{stabUploadProgress < 100 ? `Uploading... ${stabUploadProgress}%` : 'Stabilizing...'}</>
                ) : (
                  <><Lock className="w-5 h-5" /> Apply Steady Frame — {STABILIZE_CREDITS} Credits</>
                )}
              </button>
              <GuestCTA />
              <p className="text-xs text-gray-500 text-center">
                Steady Frame: Optimized for subtle camera drift and zoom in AI-generated ambiance videos.
                Locks the camera to frame 1's coordinates for a true tripod effect.
              </p>
              {atConcurrentLimit && (
                <p className="text-sm text-amber-400 text-center">
                  Concurrent generation limit reached ({maxConcurrent}). Wait for current renders to finish, or upgrade.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ===== BEFORE / AFTER PREVIEW (marketing demo) ===== */}
        <div className="max-w-6xl mx-auto mt-10" data-testid="steady-frame-preview">
          <div className="text-center mb-6">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">See the difference</h3>
            <p className="text-sm text-gray-400">A real before / after on AI-generated camera drift.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="relative bg-[#1A2035] rounded-2xl border border-[#222D4A] overflow-hidden" data-testid="steady-frame-before-preview">
              <div className="aspect-video w-full bg-gradient-to-br from-[#3a2520] via-[#5c3a2e] to-[#1a0e0a] flex items-center justify-center">
                <div className="text-center">
                  <div className="text-xs uppercase tracking-widest text-amber-300/70 mb-2">Placeholder</div>
                  <div className="text-gray-300/80 text-sm">before video drops here</div>
                </div>
              </div>
              <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-black/60 backdrop-blur-sm border border-white/10">
                <span className="text-xs font-bold uppercase tracking-wider text-white">Before</span>
              </div>
            </div>
            <div className="relative bg-[#1A2035] rounded-2xl border border-[#FFB238]/40 overflow-hidden" data-testid="steady-frame-after-preview">
              <div className="aspect-video w-full bg-gradient-to-br from-[#1a2a3a] via-[#2e4a5c] to-[#0a1418] flex items-center justify-center">
                <div className="text-center">
                  <div className="text-xs uppercase tracking-widest text-[#FFB238]/80 mb-2">Placeholder</div>
                  <div className="text-gray-300/80 text-sm">after video drops here</div>
                </div>
              </div>
              <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#FFB238] shadow-lg">
                <span className="text-xs font-bold uppercase tracking-wider text-[#0F1419]">After</span>
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      {/* ===== IMAGE GENERATOR TAB ===== */}
      {activeTab === 'image' && (
        <Suspense fallback={<TabLoading />}>
          <ImageGeneratorTab
            imgPrompt={imgPrompt} setImgPrompt={setImgPrompt}
            enhancing={enhancing} onEnhance={handleEnhancePrompt}
            checkAuth={checkAuth}
            imgAspectRatio={imgAspectRatio} setImgAspectRatio={setImgAspectRatio}
            imgResolution={imgResolution} setImgResolution={setImgResolution}
            imgStyle={imgStyle} setImgStyle={setImgStyle}
            imgCreditCost={imgCreditCost}
            user={user}
            imgGenerating={imgGenerating} onGenerate={handleImageGenerate}
            atConcurrentLimit={atConcurrentLimit} promptLocked={promptLocked}
            maxConcurrent={maxConcurrent}
            GuestCTA={GuestCTA}
          />
        </Suspense>
      )}

      {/* ===== EXAMPLE VIDEO PREVIEW MODAL ===== */}
      {activeTab !== 'sound' && previewExampleVideo && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setPreviewExampleVideo(null)} data-testid="example-video-modal">
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewExampleVideo(null)}
              className="absolute -top-12 right-0 p-2 text-white hover:text-[#FFB238] transition-colors"
              data-testid="close-example-preview"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="bg-[#0F1419] rounded-2xl overflow-hidden border border-[#222D4A]">
              <video
                src={previewExampleVideo.url}
                controls
                autoPlay
                loop
                controlsList="nodownload noplaybackrate noremoteplayback"
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
                className="w-full aspect-video"
                data-testid="example-video-player"
              />
              <div className="p-4">
                <p className="text-gray-400 text-sm">{previewExampleVideo.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== ATMOSPHERIC OVERLAYS TAB ===== */}
      {activeTab === 'atmosphere' && (
        <Suspense fallback={<TabLoading />}>
        <AtmosphereTab
          user={user}
          token={token}
          onAuthRequired={() => setShowAuthModal(true)}
          onLowCredits={(need) => { setCreditsNeeded(need); setShowTopupModal(true); }}
          onStarted={() => {
            setSuccessMessage('Atmospheric overlay processing started! Usually takes 1-3 minutes. The finished video appears in My Videos.');
            setShowSuccessModal(true);
            fetchUser();
          }}
          onActiveRefresh={fetchGenerationStatus}
        />
        </Suspense>
      )}

      {/* ===== 4K UPSCALE TAB ===== */}
      {activeTab === 'upscale' && (
        <Suspense fallback={<TabLoading />}>
        <Upscale4KTab
          user={user}
          token={token}
          onAuthRequired={() => setShowAuthModal(true)}
          onLowCredits={(need) => { setCreditsNeeded(need); setShowTopupModal(true); }}
          onStarted={() => {
            setSuccessMessage('4K upscale started! Usually takes 2-5 minutes. The finished video appears in My Videos.');
            setShowSuccessModal(true);
            fetchUser();
          }}
        />
        </Suspense>
      )}

      {/* ===== IMAGE TRANSFORMATION TAB (Gemini 3 Pro Image) ===== */}
      {activeTab === 'transform' && (
        <Suspense fallback={<TabLoading />}>
        <TransformTab
          user={user}
          token={token}
          onAuthRequired={() => setShowAuthModal(true)}
          onLowCredits={(need) => { setCreditsNeeded(need); setShowTopupModal(true); }}
          preloadedTransform={preloadedTransform}
          onClearPreload={() => setPreloadedTransform(null)}
          onSuccess={() => {
            setSuccessMessage('Your image is being transformed. It will appear in your Gallery in ~15-30 seconds.');
            setShowSuccessModal(true);
            fetchUser();
          }}
        />
        </Suspense>
      )}

      {/* ===== SOUND GENERATOR TAB ===== */}
      {activeTab === 'sound' && (
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
          {/* Left: Sound Settings */}
          <div className="bg-[#1A2035] rounded-2xl p-5 sm:p-8 border border-[#222D4A]" data-testid="sound-settings">
            <h2 className="text-xl font-bold text-white mb-6">Sound Effects Generator</h2>

            {/* Prompt */}
            <div className="mb-6">
              <label className="block text-white font-medium mb-2 text-sm">Describe your perfect sound</label>
              <textarea value={soundPrompt} onChange={(e) => setSoundPrompt(e.target.value)}
                onFocus={() => { if (!checkAuth()) document.activeElement.blur(); }}
                placeholder="Crackling fireplace, gentle rain on a window, distant thunder, wind through trees..."
                className="w-full px-4 py-3 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none resize-none" rows={3}
                data-testid="sound-prompt-input" />
              <p className="text-xs text-gray-500 mt-2">Describe the ambient sounds you want. Be specific about intensity and mood.</p>
            </div>

            {/* Duration */}
            <div className="mb-6">
              <label className="block text-gray-400 mb-3 text-sm">Duration</label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { sec: 10, label: '10s', credits: 30 },
                  { sec: 15, label: '15s', credits: 40 },
                  { sec: 20, label: '20s', credits: 50 },
                  { sec: 60, label: '1 min', credits: 70 },
                  { sec: 300, label: '5 min', credits: 120 },
                  { sec: 1200, label: '20 min', credits: 200 }
                ].map(tier => (
                  <button key={tier.sec} onClick={() => setSoundDuration(tier.sec)}
                    className={`px-4 py-3 rounded-lg border transition-all text-center ${
                      soundDuration === tier.sec
                        ? 'bg-[#FFB238] text-[#0F1419] border-[#FFB238] font-bold'
                        : 'bg-[#222D4A] text-gray-300 border-[#2A3550] hover:border-[#FFB238]/50'
                    }`}
                    data-testid={`sound-duration-${tier.sec}`}
                  >
                    <span className="block font-medium">{tier.label}</span>
                    <span className={`text-xs ${soundDuration === tier.sec ? 'text-[#0F1419]/70' : 'text-gray-500'}`}>{tier.credits} credits</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Generate Button */}
            <button onClick={handleSoundGenerate}
              disabled={soundGenerating || !soundPrompt.trim() || atConcurrentLimit || promptLocked}
              className="w-full px-6 py-4 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold text-lg hover:bg-[#FFDA82] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="generate-sound-button">
              {promptLocked ? 'Account Temporarily Locked' : soundGenerating ? 'Generating Sound...' : `Generate Sound — ${SOUND_CREDITS[soundDuration]} Credits`}
            </button>
            <GuestCTA />
            <div className="flex items-center gap-1.5 justify-center mt-1 group relative">
              <Info className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-xs text-gray-500">Content policy applies</span>
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-[#222D4A] border border-[#2A3550] text-gray-300 text-xs rounded-lg px-3 py-2 w-64 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                To comply with copyright laws, we do not allow the generation of trademarked characters or brands.
              </div>
            </div>
          </div>

          {/* Right: Summary */}
          <div className="bg-[#1A2035] rounded-2xl p-5 sm:p-8 border border-[#222D4A]" data-testid="sound-generation-summary">
            <h3 className="text-lg font-bold text-white mb-6">Generation Summary</h3>
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Sound Generation ({soundDuration >= 60 ? (soundDuration >= 1200 ? '20 min' : soundDuration >= 300 ? '5 min' : '1 min') : `${soundDuration}s`})</span>
                <span className="text-white font-medium">{SOUND_CREDITS[soundDuration]} credits</span>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-[#222D4A]">
                <span className="text-white font-medium">Total cost</span>
                <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FFB238]">
                  <Zap className="w-4 h-4 text-[#0F1419]" fill="#0F1419" />
                  <span className="text-sm font-bold text-[#0F1419]">{SOUND_CREDITS[soundDuration]} Credits</span>
                </div>
              </div>
              <div className="flex items-center justify-between"><span className="text-gray-400">Your balance</span><span className="text-white font-medium">{user?.credits_balance || 0} credits</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-400">Duration</span><span className="text-white font-medium">{soundDuration} seconds</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-400">Loop</span><span className="text-green-400 font-medium">Seamless auto-loop</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-400">Output</span><span className="text-white font-medium">MP3 audio</span></div>
            </div>
          </div>
        </div>
      )}


      {/* ===== 5-STAR RATING BAR (verified creators) ===== */}
      {userHasRated !== false && (
        <RatingBar onRated={() => setUserHasRated(true)} />
      )}

      {/* Prominent rating prompt — shown below the service, before FAQ, until the user rates */}
      {userHasRated === false && (
        <div className="mt-16">
          <RatingBar prominent onRated={() => setUserHasRated(true)} />
        </div>
      )}

      {/* ===== FAQ SECTION ===== */}
      <div className="mt-20 max-w-3xl mx-auto mb-8" data-testid="faq-section">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Frequently Asked Questions</h2>
          <p className="text-gray-400">Everything you need to know about Visual Atmos.</p>
        </div>

        {/* Getting the Best Results */}
        <h3 className="text-lg font-semibold text-[#FFB238] mb-4 mt-8">Getting the Best Results</h3>
        <div className="space-y-3 mb-8">
          {[
            { q: "What kind of images work best for video generation?", a: "Upload images that already contain the elements you want animated. Our engine brings existing details — like a fireplace, rain on a window, or a flickering candle — to life, and it works best when those elements are already clearly visible in your image." },
            { q: "Do you guarantee the results?", a: "No — this is AI, and it isn't perfect. Results vary and can be unpredictable, so a video won't always match your prompt exactly (for example, an effect like rain may not appear every time). The good news is the technology is improving rapidly and getting better by the day. If a result isn't quite what you hoped for, try adjusting your image or wording and generating again." },
            { q: "How long can my video be?", a: "You choose any length from 4 to 15 seconds with the duration slider, for both Steady Vibe and Superior Motion. Credits are charged per second, so shorter clips cost less." },
            { q: "How does the Seamless Loop tool work?", a: "It automatically blends the end of your video back into the beginning, allowing you to play the background on an infinite repeat without any noticeable cuts or jumps. Perfect for YouTube ambiance channels." }
          ].map((item, idx) => (
            <div
              key={`results-${idx}`}
              className="bg-[#1A2035] rounded-xl border border-[#222D4A] overflow-hidden"
              data-testid={`faq-item-results-${idx}`}
            >
              <button
                onClick={() => setOpenFaq(openFaq === `r${idx}` ? null : `r${idx}`)}
                className="w-full flex items-center justify-between px-6 py-5 text-left"
                data-testid={`faq-toggle-results-${idx}`}
              >
                <span className="text-white font-medium pr-4">{item.q}</span>
                <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${openFaq === `r${idx}` ? 'rotate-180' : ''}`} />
              </button>
              {openFaq === `r${idx}` && (
                <div className="px-6 pb-5">
                  <p className="text-gray-400">{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Credits & Billing */}
        <h3 className="text-lg font-semibold text-[#FFB238] mb-4">Credits & Billing</h3>
        <div className="space-y-3 mb-8">
          {[
            { q: "How do credits work?", a: "Our platform uses credits instead of strict video limits. Video generation is charged per second of video (Steady Vibe from 20 credits/second and Superior Motion from 45 credits/second, depending on the resolution you pick), looping is free for all signed-in users, and image generation costs 20-60 credits. You receive a fresh batch of credits every month based on your plan." },
            { q: "Do my unused credits roll over?", a: "Monthly subscription credits reset at the start of your billing cycle. However, credits bought via a one-time \"Top-Up Pack\" never expire and roll over indefinitely while your account is active." },
            { q: "What happens if I run out of credits?", a: "You don't have to upgrade your monthly plan! You can simply purchase a one-time Credit Top-Up to get the exact amount you need to finish your project." }
          ].map((item, idx) => (
            <div
              key={`billing-${idx}`}
              className="bg-[#1A2035] rounded-xl border border-[#222D4A] overflow-hidden"
              data-testid={`faq-item-billing-${idx}`}
            >
              <button
                onClick={() => setOpenFaq(openFaq === `b${idx}` ? null : `b${idx}`)}
                className="w-full flex items-center justify-between px-6 py-5 text-left"
                data-testid={`faq-toggle-billing-${idx}`}
              >
                <span className="text-white font-medium pr-4">{item.q}</span>
                <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${openFaq === `b${idx}` ? 'rotate-180' : ''}`} />
              </button>
              {openFaq === `b${idx}` && (
                <div className="px-6 pb-5">
                  <p className="text-gray-400">{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Storage & Usage */}
        <h3 className="text-lg font-semibold text-[#FFB238] mb-4">Storage & Usage</h3>
        <div className="space-y-3">
          {[
            { q: "How long are my files saved in the gallery?", a: "To keep the platform fast, your generated videos and images are safely stored in your cloud gallery for 90 days after creation. Please download your final masterpieces to your device before then so you always have your own copy!" },
            { q: "Can I use these videos commercially (like on YouTube)?", a: "Yes! Every single subscription tier includes a full Commercial Usage License. You are completely free to monetize your generated ambiance videos on YouTube, social media, or other projects." }
          ].map((item, idx) => (
            <div
              key={`storage-${idx}`}
              className="bg-[#1A2035] rounded-xl border border-[#222D4A] overflow-hidden"
              data-testid={`faq-item-storage-${idx}`}
            >
              <button
                onClick={() => setOpenFaq(openFaq === `s${idx}` ? null : `s${idx}`)}
                className="w-full flex items-center justify-between px-6 py-5 text-left"
                data-testid={`faq-toggle-storage-${idx}`}
              >
                <span className="text-white font-medium pr-4">{item.q}</span>
                <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${openFaq === `s${idx}` ? 'rotate-180' : ''}`} />
              </button>
              {openFaq === `s${idx}` && (
                <div className="px-6 pb-5">
                  <p className="text-gray-400">{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ===== CONTACT FORM ===== */}
      <div className="mt-20 max-w-2xl mx-auto mb-8 px-4" data-testid="contact-section">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Get in Touch</h2>
          <p className="text-gray-400">Running into issues or have a suggestion? We'd love to hear from you.</p>
        </div>
        <div className="bg-[#1A2035] rounded-2xl p-6 sm:p-8 border border-[#222D4A]">
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Your Name</label>
                <input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)}
                  placeholder="John Doe"
                  className="w-full px-4 py-3 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none"
                  data-testid="contact-name-input" />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-2">Email Address</label>
                <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none"
                  data-testid="contact-email-input" />
              </div>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-2">Message</label>
              <textarea value={contactMessage} onChange={(e) => setContactMessage(e.target.value)}
                placeholder="Describe your issue or share your feedback..."
                className="w-full px-4 py-3 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none resize-none" rows={4}
                data-testid="contact-message-input" />
            </div>
            <button
              onClick={async () => {
                if (!contactName.trim() || !contactEmail.trim() || !contactMessage.trim()) {
                  toast.error('Please fill in all fields');
                  return;
                }
                setContactSending(true);
                try {
                  await axios.post(`${API}/contact`, { name: contactName, email: contactEmail, message: contactMessage });
                  toast.success('Message sent! We\'ll get back to you soon.');
                  setContactName(''); setContactEmail(''); setContactMessage('');
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
                } finally {
                  setContactSending(false);
                }
              }}
              disabled={contactSending || !contactName.trim() || !contactEmail.trim() || !contactMessage.trim()}
              className="w-full px-6 py-4 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold text-lg hover:bg-[#FFDA82] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="contact-submit-button"
            >
              {contactSending ? 'Sending...' : 'Send Message'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Generate;
